'use client'

import { isNil, isUndefined } from 'lodash-es'
import { useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
} from 'react'

import type { TLastActiveContract } from '@/entities/contract'
import { useRouter } from '@/shared/i18n'
import {
  DateRange,
  formatDate,
  getURLWithParams,
  OpenOptions,
  parseDate,
} from '@/shared/lib'
import { AlertDialog, Separator } from '@/shared/ui'
import {
  PeriodCalendarDialog,
  useContractPeriod,
  DATE_FORMAT,
  getDefaultPeriod,
} from '@/widgets/contract-calendar'

import { useLastActiveContract } from '../../../api/get-last-active-contract'
import {
  ContractDialog,
  DIALOG,
  useContractDialog,
} from '../../../lib/contract-dialog'
import { CheckExtendContractDialog } from '../dialog/check-extend-contract'
import { EndDateCalendarDialog } from '../dialog/end-date-calendar'
import { InProgressContractDialog } from '../dialog/in-progress-contract'

type ContractState = {
  period: DateRange
  hasPeriodSelected: boolean
  updatePeriod: (period: DateRange) => void
  openDialog: (key: ContractDialog, options?: OpenOptions) => void
  lastActiveContract?: TLastActiveContract | null
  getLastActiveContract: () => Promise<{
    success: boolean
    data: TLastActiveContract | null
  }>
}

const ContractContext = createContext<ContractState | null>(null)

export const useContract = () => {
  const context = useContext(ContractContext)

  if (!context) {
    throw new Error('useContract must be used within a ContractProvider')
  }

  return context
}

export const getRequestContractUrl = ({
  roomId,
  lastActiveContract,
  dateRange,
}: {
  roomId: number
  lastActiveContract?: TLastActiveContract
  dateRange: DateRange
}) => {
  const defaultURL = `/guest/contract/request`

  return getURLWithParams(defaultURL, {
    rid: roomId.toString(),
    startDate: formatDate({ date: dateRange.from, format: DATE_FORMAT }),
    endDate: formatDate({ date: dateRange.to, format: DATE_FORMAT }),
    ...(lastActiveContract?.initialContractId && {
      initialContractId: lastActiveContract.initialContractId.toString(),
      extend: 'true',
    }),
  })
}

type Props = {
  roomId: number
}

export const Provider = ({ roomId, children }: PropsWithChildren<Props>) => {
  const t = useTranslations('common')

  const router = useRouter()
  const searchParams = useSearchParams()

  const { period, hasPeriodSelected, updatePeriod } = useContractPeriod({
    defaultPeriod: getDefaultPeriod(searchParams),
  })

  const { lastActiveContract, fetchLastActiveContract } = useLastActiveContract(
    { roomId },
  )

  const { isOpen, openDialog, close, closeHandler } = useContractDialog({
    roomId,
    updatePeriod,
    lastActiveContract,
  })

  const getLastActiveContract = useCallback(async () => {
    if (isUndefined(lastActiveContract)) {
      return await fetchLastActiveContract()
    }

    return { success: true, data: lastActiveContract }
  }, [lastActiveContract, fetchLastActiveContract])

  const handleEndDateCalendarSelect = (date?: Date) => {
    if (!date || isNil(lastActiveContract)) {
      return null
    }

    const startDate = parseDate({
      value: lastActiveContract.extendContractStartDate,
      format: DATE_FORMAT,
    })

    router.push(
      getRequestContractUrl({
        roomId,
        lastActiveContract,
        dateRange: { from: startDate, to: date },
      }),
    )
  }

  const handlePeriodCalendarSelect = (dateRange: DateRange) => {
    if (!dateRange.from || !dateRange.to) {
      return null
    }

    if (lastActiveContract?.isExtendContractAvailable) {
      router.push(
        getRequestContractUrl({
          roomId,
          dateRange,
        }),
      )
    } else {
      updatePeriod(dateRange)
      close(DIALOG.PERIOD_CALENDAR)
    }
  }

  const value = useMemo(
    () => ({
      period,
      hasPeriodSelected,
      updatePeriod,
      openDialog,
      lastActiveContract,
      getLastActiveContract,
    }),
    [
      period,
      hasPeriodSelected,
      updatePeriod,
      openDialog,
      lastActiveContract,
      getLastActiveContract,
    ],
  )

  return (
    <ContractContext.Provider value={value}>
      {children}

      <CheckExtendContractDialog
        isOpen={isOpen(DIALOG.CHECK_EXTEND_CONTRACT)}
        onOpenChange={closeHandler(DIALOG.CHECK_EXTEND_CONTRACT)}
      />

      <InProgressContractDialog
        isOpen={isOpen(DIALOG.IN_PROGRESS_CONTRACT)}
        onOpenChange={closeHandler(DIALOG.IN_PROGRESS_CONTRACT)}
      />

      <EndDateCalendarDialog
        isOpen={isOpen(DIALOG.END_DATE_CALENDAR)}
        onOpenChange={closeHandler(DIALOG.END_DATE_CALENDAR)}
        roomId={roomId}
        startDate={parseDate({
          value: lastActiveContract?.extendContractStartDate ?? '',
          format: 'yyyy-MM-dd',
        })}
        onSelect={handleEndDateCalendarSelect}
      />

      <PeriodCalendarDialog
        isOpen={isOpen(DIALOG.PERIOD_CALENDAR)}
        onOpenChange={closeHandler(DIALOG.PERIOD_CALENDAR)}
        roomId={roomId}
        period={period}
        onSelect={handlePeriodCalendarSelect}
      />

      <AlertDialog
        isOpen={isOpen(DIALOG.BLOCKED_USER)}
        onOpenChange={closeHandler(DIALOG.BLOCKED_USER)}
        title={t('dialog.title')}
        content={t('dialog.description')}
        confirmText={t('dialog.button.confirm')}
      />
    </ContractContext.Provider>
  )
}

export const Container = ({ children }: PropsWithChildren) => {
  const { hasPeriodSelected } = useContract()

  if (hasPeriodSelected) {
    return <div className="bg-gray-0 my-6 p-6">{children}</div>
  }

  return <Separator color="light-gray" className="my-8.5" />
}
